// API for Akaun (Penyewa) data
import { API_BASE_URL, getUserDepartment } from "./base";
import { withCsrfHeaders } from "./csrf";
import { apiFetch, apiJson } from './apiFetch';

// Helper function to get user department and create headers
// NO LONGER defaults to UEU - will throw error if session invalid
const getApiHeaders = () => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const userDepartment = getUserDepartment(); // Throws if missing - no UEU fallback
  return {
    "Content-Type": "application/json",
    "x-bahagian-pemantau": userDepartment,
    "X-User-Department": userDepartment,
    "x-id-pengguna": (user.id_pengguna || "").toLowerCase(),
  };
};

// Get akaun by no_akaun (ignores department filter, for admin/special use)
export const apiGetAkaunAllByNoAkaun = async (noAkaun) => {
  try {
    const response = await fetch(`${API_BASE_URL}/akaun/all-by-no-akaun?no_akaun=${encodeURIComponent(noAkaun)}`, {
      method: "GET",
      headers: getApiHeaders(),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching accounts by no_akaun (all):", error);
    throw error;
  }
};

// Robust detail by no_akaun (ignores department, normalizes formatting)
export const apiGetAkaunDetailByNoAkaun = async (noAkaun) => {
  try {
    const response = await fetch(`${API_BASE_URL}/akaun/detail-by-no-akaun?no_akaun=${encodeURIComponent(noAkaun)}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching akaun detail by no_akaun:", error);
    throw error;
  }
};

// Batch fetch minimal account details by list of no_akaun
export const apiGetAkaunBatchByNoAkaun = async (noAkauns = []) => {
  const list = Array.isArray(noAkauns) ? noAkauns.filter(Boolean) : [];
  if (!list.length) return { data: [] };
  const qs = encodeURIComponent(list.join(','));
  const response = await fetch(`${API_BASE_URL}/akaun/batch-by-no-akaun?noAkauns=${qs}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) throw new Error('Failed to fetch batch akaun');
  return response.json();
};

// Get all accounts with pagination
export const apiGetAllAkaun = async (page = 1, limit = 10) => {
  try {
    const url = `${API_BASE_URL}/akaun?page=${page}&limit=${limit}`;
    console.log("Making request to:", url);

    const headers = getApiHeaders();
    console.log("Request headers:", headers);

    const response = await apiFetch(url, { method: 'GET', headers });

    console.log("Response status:", response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Error response:", errorText);
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    console.log("Success! Received data:", data);
    return data;
  } catch (error) {
    console.error("Error fetching accounts:", error);
    console.error("Error details:", {
      message: error.message,
      name: error.name,
      stack: error.stack,
    });
    throw error;
  }
};

// Get account count
export const apiGetAkaunCount = async () => {
  try {
    const response = await apiFetch(`${API_BASE_URL}/akaun/count`, { method: 'GET', headers: getApiHeaders() });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching account count:", error);
    throw error;
  }
};

// Search accounts (now uses /akaun/search)
export const apiSearchAkaun = async (searchParams, page = 1, limit = 10) => {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    // Use only DB column names for all supported search fields
    if (searchParams.nama) queryParams.append("nama", searchParams.nama);
    if (searchParams.nokp) queryParams.append("nokp", searchParams.nokp);
    if (searchParams.kod_pelanggan) queryParams.append("kod_pelanggan", searchParams.kod_pelanggan);
    if (searchParams.no_akaun) queryParams.append("no_akaun", searchParams.no_akaun);
    if (searchParams.zon) queryParams.append("zon", searchParams.zon);
    if (searchParams.kategori_kutipan)
      queryParams.append("kategori_kutipan", searchParams.kategori_kutipan);
    if (searchParams.bahagian_pemantau)
      queryParams.append("bahagian_pemantau", searchParams.bahagian_pemantau);
    if (searchParams.status_akaun) queryParams.append("status_akaun", searchParams.status_akaun);

    const response = await apiFetch(`${API_BASE_URL}/akaun/search?${queryParams}`, { method: 'GET', headers: getApiHeaders() });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error searching accounts:", error);
    throw error;
  }
};

// LIKE-based search for UEU: nama, nokp/no_kp, no_akaun with partial matches
export const apiSearchAkaunLike = async (searchParams, page = 1, limit = 10) => {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (searchParams.nama) queryParams.append("nama", searchParams.nama);
    if (searchParams.nokp || searchParams.no_kp)
      queryParams.append("nokp", (searchParams.nokp || searchParams.no_kp));
    if (searchParams.no_akaun || searchParams.noakaun)
      queryParams.append("no_akaun", (searchParams.no_akaun || searchParams.noakaun));
    // cache-busting to prevent 304 Not Modified disrupting fetch handling
    queryParams.append("_", Date.now().toString());

    const response = await fetch(`${API_BASE_URL}/akaun/search-like?${queryParams}`, {
      method: "GET",
      headers: getApiHeaders(),
      credentials: "include",
      cache: "no-store",
    });

    // Some proxies can return 304 for cached GETs; treat as empty result rather than error
    if (response.status === 304) {
      return { data: [], pagination: { currentPage: page, itemsPerPage: limit, totalItems: 0, totalPages: 1 } };
    }
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error searching accounts (LIKE):", error);
    throw error;
  }
};

// LIKE-based search across ALL departments (ignores bahagian filter)
export const apiSearchAkaunLikeAll = async (searchParams, page = 1, limit = 10) => {
  try {
    const queryParams = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    if (searchParams.nama) queryParams.append("nama", searchParams.nama);
    if (searchParams.nokp || searchParams.no_kp)
      queryParams.append("nokp", searchParams.nokp || searchParams.no_kp);
    if (searchParams.no_akaun || searchParams.noakaun)
      queryParams.append("no_akaun", searchParams.no_akaun || searchParams.noakaun);
    queryParams.append("_", Date.now().toString());

    const response = await fetch(`${API_BASE_URL}/akaun/search-like-all?${queryParams}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      cache: "no-store",
    });
    if (response.status === 304) {
      return { data: [], pagination: { currentPage: page, itemsPerPage: limit, totalItems: 0, totalPages: 1 } };
    }
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error searching accounts (LIKE ALL):", error);
    throw error;
  }
};

// Create new account
export const apiCreateAkaun = async (accountData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/akaun`, {
      method: "POST",
      headers: await withCsrfHeaders({
        "Content-Type": "application/json",
      }),
      body: JSON.stringify(accountData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error creating account:", error);
    throw error;
  }
};

// Inline edit for specific field (uses id_nama for nama updates)
export const apiInlineEditAkaun = async (id, field, value, confirmSwap = false, replacementKodHarta = null) => {
  try {
    const response = await fetch(`${API_BASE_URL}/akaun/${id}/inline-edit`, {
      method: "PUT",
      headers: await withCsrfHeaders(getApiHeaders()),
      body: JSON.stringify({ field, value, confirmSwap, replacementKodHarta }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error inline editing account:", error);
    throw error;
  }
};

// Check impact of nama edit (how many accounts will be affected)
export const apiCheckNamaImpact = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/akaun/${id}/nama-impact`, {
      method: "GET",
      headers: getApiHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error checking nama impact:", error);
    throw error;
  }
};

// Update account
export const apiUpdateAkaun = async (id, accountData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/akaun/${id}`, {
      method: "PUT",
      headers: await withCsrfHeaders(getApiHeaders()),
      body: JSON.stringify(accountData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error updating account:", error);
    throw error;
  }
};

// Delete account
export const apiDeleteAkaun = async (id) => {
  try {
    const response = await fetch(`${API_BASE_URL}/akaun/${id}`, {
      method: "DELETE",
      headers: await withCsrfHeaders({
        "Content-Type": "application/json",
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error deleting account:", error);
    throw error;
  }
};

// Get accounts by nokp
export const apiGetAkaunByNokp = async (nokp) => {
  try {
    const response = await fetch(`${API_BASE_URL}/akaun/nokp/${encodeURIComponent(nokp)}`, {
      method: "GET",
      headers: getApiHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching accounts by nokp:", error);
    throw error;
  }
};

// Get accounts by kod harta
export const apiGetAkaunByKodHarta = async (kodHarta) => {
  try {
    const response = await fetch(`${API_BASE_URL}/akaun/kodharta/${encodeURIComponent(kodHarta)}`, {
      method: "GET",
      headers: getApiHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching accounts by kod harta:", error);
    throw error;
  }
};

// Get accounts for print report based on zone and status
export const apiGetAkaunForPrint = async (filters = {}) => {
  try {
    const queryParams = new URLSearchParams();

    // Add filters if provided
    if (filters.zonharta) {
      queryParams.append("zonharta", filters.zonharta);
    }
    if (filters.statusakaun) {
      queryParams.append("statusakaun", filters.statusakaun);
    }

    // Get all data for printing (no pagination)
    queryParams.append("all", "true");

    const response = await fetch(`${API_BASE_URL}/akaun/print?${queryParams}`, {
      method: "GET",
      headers: getApiHeaders(),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching accounts for print:", error);
    throw error;
  }
};

// Check if kod_harta is available for new rental
export const apiCheckKodHartaAvailability = async (kodHarta) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/akaun/check-availability/${encodeURIComponent(kodHarta)}`,
      {
        method: "GET",
        headers: getApiHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error checking kod_harta availability:", error);
    throw error;
  }
};

// Fallback function that returns empty data structure
export const apiGetAkaunFallback = async () => {
  console.warn("Using fallback data for accounts");
  return {
    data: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 10,
      hasNextPage: false,
      hasPreviousPage: false,
    },
  };
};

// Update akaun and harta records separately (for EditPenyewa)
export const apiUpdateAkaunAndHarta = async (id, saveData) => {
  try {
    const results = {};

    // Update akaun record
    if (saveData.akaunData) {
      console.log("Updating akaun data:", saveData.akaunData);
      const akaunResponse = await fetch(`${API_BASE_URL}/akaun/${id}`, {
        method: "PUT",
        headers: await withCsrfHeaders(getApiHeaders()),
        body: JSON.stringify(saveData.akaunData),
      });

      if (!akaunResponse.ok) {
        throw new Error(`Akaun update failed! status: ${akaunResponse.status}`);
      }

      results.akaun = await akaunResponse.json();
    }

    // Update harta record if harta data provided
    if (saveData.hartaData && saveData.hartaData.kod_harta) {
      console.log("Updating harta data:", saveData.hartaData);
      const hartaResponse = await fetch(
        `${API_BASE_URL}/harta/kod/${encodeURIComponent(saveData.hartaData.kod_harta)}`,
        {
          method: "PUT",
          headers: await withCsrfHeaders({
            "Content-Type": "application/json",
            ...getApiHeaders(),
          }),
          body: JSON.stringify(saveData.hartaData),
        }
      );

      if (!hartaResponse.ok) {
        throw new Error(`Harta update failed! status: ${hartaResponse.status}`);
      }

      results.harta = await hartaResponse.json();
    }

    return results;
  } catch (error) {
    console.error("Error updating akaun and harta:", error);
    throw error;
  }
};

// Get accounts by no_akaun
export const apiGetAkaunByNoAkaun = async (noAkaun) => {
  try {
    const response = await fetch(`${API_BASE_URL}/akaun?no_akaun=${encodeURIComponent(noAkaun)}`, {
      method: "GET",
      headers: getApiHeaders(),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching accounts by no_akaun:", error);
    throw error;
  }
};

// Get accounts by kod_pelanggan
export const apiGetAkaunByKodPelanggan = async (kodPelanggan) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/akaun?kod_pelanggan=${encodeURIComponent(kodPelanggan)}`,
      {
        method: "GET",
        headers: getApiHeaders(),
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching accounts by kod_pelanggan:", error);
    throw error;
  }
};

// Check if kod_harta is being used by another akaun
export const apiCheckKodHartaUsage = async (kodHarta, currentAkaunId) => {
  try {
    const url = currentAkaunId 
      ? `${API_BASE_URL}/akaun/check-kodharta-usage/${encodeURIComponent(kodHarta)}?currentAkaunId=${currentAkaunId}`
      : `${API_BASE_URL}/akaun/check-kodharta-usage/${encodeURIComponent(kodHarta)}`;
    
    const response = await fetch(url, {
      method: "GET",
      headers: getApiHeaders(),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error checking kod_harta usage:", error);
    throw error;
  }
};
